// Production-ready logging utility to replace console statements

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

export interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  timestamp?: string;
  sessionId?: string;
  metric?: string;
  additionalData?: Record<string, unknown>;
}

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel;
  private isProduction: boolean;
  private sessionId: string;

  private constructor() {
    // Vite exposes mode via import.meta.env, not process.env
    this.isProduction = import.meta.env.MODE === 'production';
    this.logLevel = this.isProduction ? LogLevel.WARN : LogLevel.DEBUG;
    this.sessionId = this.generateSessionId();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.logLevel;
  }

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const sessionInfo = context?.sessionId || this.sessionId;
    const componentInfo = context?.component ? `[${context.component}]` : '';
    const actionInfo = context?.action ? `{${context.action}}` : '';
    
    return `${timestamp} ${level} ${sessionInfo} ${componentInfo}${actionInfo} ${message}`;
  }

  private logToConsole(level: LogLevel, message: string, context?: LogContext, data?: unknown): void {
    if (!this.shouldLog(level)) return;

    const levelNames = ['ERROR', 'WARN', 'INFO', 'DEBUG', 'TRACE'];
    const levelName = levelNames[level];
    const formattedMessage = this.formatMessage(levelName, message, context);

    const consoleMethods = ['error', 'warn', 'info', 'log', 'log'] as const;
    const consoleMethod = consoleMethods[level];

    if (this.isProduction && level > LogLevel.INFO) {
      // Don't log debug/trace in production
      return;
    }

    if (data) {
      console[consoleMethod](formattedMessage, data);
    } else {
      console[consoleMethod](formattedMessage);
    }

    // In production, send to external logging service
    if (this.isProduction && level <= LogLevel.WARN) {
      this.sendToLoggingService(level, message, context, data);
    }
  }

  private sendToLoggingService(level: LogLevel, message: string, context?: LogContext, data?: unknown): void {
    // In production, send to logging services like DataDog, Splunk, etc.
    if (typeof window !== 'undefined') {
      const logEntry = {
        level: LogLevel[level],
        message,
        context: {
          ...context,
          timestamp: new Date().toISOString(),
          sessionId: this.sessionId,
          url: window.location.href,
          userAgent: navigator.userAgent
        },
        data
      };

      fetch('/api/logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logEntry)
      }).catch(() => {
        // Silently fail - logging shouldn't break the app
      });
    }
  }

  // Public logging methods
  error(message: string, context?: LogContext, data?: unknown): void {
    this.logToConsole(LogLevel.ERROR, message, context, data);
  }

  warn(message: string, context?: LogContext, data?: unknown): void {
    this.logToConsole(LogLevel.WARN, message, context, data);
  }

  info(message: string, context?: LogContext, data?: unknown): void {
    this.logToConsole(LogLevel.INFO, message, context, data);
  }

  debug(message: string, context?: LogContext, data?: unknown): void {
    this.logToConsole(LogLevel.DEBUG, message, context, data);
  }

  trace(message: string, context?: LogContext, data?: unknown): void {
    this.logToConsole(LogLevel.TRACE, message, context, data);
  }

  // Performance logging
  time(label: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.time(this.formatMessage('PERF', `START ${label}`, context));
    }
  }

  timeEnd(label: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.timeEnd(this.formatMessage('PERF', `END ${label}`, context));
    }
  }

  // Group logging for related operations
  group(label: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.group(this.formatMessage('GROUP', label, context));
    }
  }

  groupEnd(): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.groupEnd();
    }
  }

  // Configuration methods
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  getLogLevel(): LogLevel {
    return this.logLevel;
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience functions for common scenarios
export const logAuth = (message: string, data?: unknown) =>
  logger.info(message, { component: 'Auth' }, data);

export const logAPI = (message: string, action: string, data?: unknown) =>
  logger.debug(message, { component: 'API', action }, data);

export const logUI = (message: string, component: string, data?: unknown) =>
  logger.debug(message, { component }, data);

export const logPerformance = (message: string, data?: unknown) =>
  logger.info(message, { component: 'Performance' }, data);

export const logNavigation = (message: string, data?: unknown) =>
  logger.debug(message, { component: 'Navigation' }, data);

export const logWebVital = (name: string, value: number, data?: unknown) =>
  logger.info(`${name}: ${value}ms`, { component: 'WebVitals' }, data);

// Development-only logging (automatically filtered in production)
export const devLog = (message: string, data?: unknown) => {
  if (import.meta.env.MODE === 'development') {
    logger.debug(message, { component: 'Dev' }, data);
  }
};